import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import InputGroup from 'react-bootstrap/InputGroup';

import {
    FaBolt,
    FaPlus,
    FaEdit,
    FaTrash,
    FaPaperPlane,
    FaHistory,
    FaCopy,
    FaEye,
    FaEyeSlash,
    FaCheck,
    FaTimes,
    FaRedo,
    FaExternalLinkAlt
} from 'react-icons/fa';

import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../lib/StAuthGiver.js';
import { callApi, getCurSiteId } from '../lib/Common_funcs.js';
import toast from 'react-hot-toast';

const WhichEnv = (process.env.REACT_APP_TURBO_ENV === undefined ? 'aws' : 'staging');
const Tdebug = (WhichEnv != 'aws');

export const StDashAutomations = () => {
    const [loading, setLoading] = useState(true);
    const [webhooks, setWebhooks] = useState([]);
    const [availableEvents, setAvailableEvents] = useState([
        { type: 'contact_form.submitted', label: 'Contact Form Submitted' }
    ]);
    const { loginAction, logOut } = useAuth();
    const isMounted = useRef(false);
    const [siteId, setSiteId] = useState(getCurSiteId());

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSecretModal, setShowSecretModal] = useState(false);
    const [selectedWebhook, setSelectedWebhook] = useState(null);

    // Form states
    const [formName, setFormName] = useState('');
    const [formUrl, setFormUrl] = useState('');
    const [formEvents, setFormEvents] = useState([]);
    const [formEnabled, setFormEnabled] = useState(true);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // New webhook secret (shown after creation)
    const [newWebhookSecret, setNewWebhookSecret] = useState('');
    const [showSecret, setShowSecret] = useState(false);
    const [secretCopied, setSecretCopied] = useState(false);

    // Logs states
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsCursor, setLogsCursor] = useState(null);
    const [hasMoreLogs, setHasMoreLogs] = useState(false);
    const [selectedLogIdx, setSelectedLogIdx] = useState(0);

    // Toggle loading state per webhook
    const [togglingWebhook, setTogglingWebhook] = useState(null);
    const [testingWebhook, setTestingWebhook] = useState(null);

    useEffect(() => {
        if (isMounted.current === false) {
            isMounted.current = true;
        } else {
            return;
        }
        fetchWebhooks();
    }, []);

    const fetchWebhooks = async () => {
        setLoading(true);
        const rcJson = await callApi('/sites/' + siteId + '/webhooks', 'GET', {}, {});
        Tdebug && console.log('fetchWebhooks:', rcJson);

        if (rcJson.disposition === 'logout') {
            logOut();
            return;
        }

        if (rcJson.httpStatus === 200 || rcJson.httpStatus === '200') {
            setWebhooks(rcJson.respBody.webhooks || []);
        } else if (rcJson.disposition === 'empty') {
            setWebhooks([]);
        } else {
            toast.error('Failed to load webhooks');
        }
        setLoading(false);
    };

    const resetForm = () => {
        setFormName('');
        setFormUrl('');
        setFormEvents([]);
        setFormEnabled(true);
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};
        if (!formName.trim()) {
            errors.name = 'Name is required';
        }
        if (!formUrl.trim()) {
            errors.url = 'URL is required';
        } else {
            try {
                new URL(formUrl);
            } catch {
                errors.url = 'Invalid URL format';
            }
        }
        if (formEvents.length === 0) {
            errors.events = 'Select at least one event';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateWebhook = async () => {
        if (!validateForm()) return;

        setFormSubmitting(true);
        const body = JSON.stringify({
            name: formName.trim(),
            url: formUrl.trim(),
            events: formEvents,
            isEnabled: formEnabled
        });

        const rcJson = await callApi('/sites/' + siteId + '/webhooks', 'POST', {}, body);
        Tdebug && console.log('createWebhook:', rcJson);

        setFormSubmitting(false);

        if (rcJson.httpStatus === 200 || rcJson.httpStatus === '200' || rcJson.httpStatus === 201 || rcJson.httpStatus === '201') {
            toast.success('Webhook created successfully');
            setShowCreateModal(false);
            resetForm();

            // Show the signing secret
            if (rcJson.respBody.signingSecret) {
                setNewWebhookSecret(rcJson.respBody.signingSecret);
                setShowSecretModal(true);
            }

            fetchWebhooks();
        } else {
            toast.error(rcJson.message || 'Failed to create webhook');
        }
    };

    const handleEditWebhook = async () => {
        if (!validateForm()) return;

        setFormSubmitting(true);
        const body = JSON.stringify({
            name: formName.trim(),
            url: formUrl.trim(),
            events: formEvents,
            isEnabled: formEnabled
        });

        const rcJson = await callApi('/sites/' + siteId + '/webhooks/' + selectedWebhook.webhookId, 'PUT', {}, body);
        Tdebug && console.log('editWebhook:', rcJson);

        setFormSubmitting(false);

        if (rcJson.httpStatus === 200 || rcJson.httpStatus === '200') {
            toast.success('Webhook updated successfully');
            setShowEditModal(false);
            resetForm();
            setSelectedWebhook(null);
            fetchWebhooks();
        } else {
            toast.error(rcJson.message || 'Failed to update webhook');
        }
    };

    const handleDeleteWebhook = async () => {
        setFormSubmitting(true);
	const sendBodyFlag = false;
        const rcJson = await callApi('/sites/' + siteId + '/webhooks/' + selectedWebhook.webhookId, 'DELETE', {}, {}, sendBodyFlag);
        Tdebug && console.log('deleteWebhook:', rcJson);

        setFormSubmitting(false);

        if (rcJson.httpStatus === 200 || rcJson.httpStatus === '200') {
            toast.success('Webhook deleted');
            setShowDeleteModal(false);
            setSelectedWebhook(null);
            fetchWebhooks();
        } else {
            toast.error(rcJson.message || 'Failed to delete webhook');
        }
    };

    const handleToggleEnabled = async (webhook) => {
        setTogglingWebhook(webhook.webhookId);
        const body = JSON.stringify({
            isEnabled: !webhook.isEnabled
        });

        const rcJson = await callApi('/sites/' + siteId + '/webhooks/' + webhook.webhookId, 'PUT', {}, body);
        Tdebug && console.log('toggleWebhook:', rcJson);

        setTogglingWebhook(null);

        if (rcJson.httpStatus === 200 || rcJson.httpStatus === '200') {
            toast.success(webhook.isEnabled ? 'Webhook disabled' : 'Webhook enabled');
            fetchWebhooks();
        } else {
            toast.error('Failed to update webhook');
        }
    };

    const handleSendTestEvent = async (webhook) => {
        setTestingWebhook(webhook.webhookId);
        const rcJson = await callApi('/sites/' + siteId + '/webhooks/' + webhook.webhookId + '/test', 'POST', {}, JSON.stringify({}));
        Tdebug && console.log('sendTestEvent:', rcJson);

        setTestingWebhook(null);

        if (rcJson.httpStatus === 200 || rcJson.httpStatus === '200') {
            toast.success(
                (t) => (
                    <div>
                        <div>Test event sent!</div>
                        <Button
                            size="sm"
                            variant="link"
                            className="p-0 mt-1"
                            onClick={() => { toast.dismiss(t.id); handleOpenLogs(webhook); }}
                        >
                            View delivery logs
                        </Button>
                    </div>
                ),
                { duration: 5000 }
            );
        } else {
            toast.error(rcJson.message || 'Failed to send test event');
        }
    };

    const handleOpenLogs = async (webhook) => {
        setSelectedWebhook(webhook);
        setLogs([]);
        setLogsCursor(null);
        setHasMoreLogs(false);
        setSelectedLogIdx(0);
        setShowLogsModal(true);
        await fetchLogs(webhook.webhookId, null);
    };

    const fetchLogs = async (webhookId, cursor) => {
        setLogsLoading(true);
        let url = '/sites/' + siteId + '/webhooks/' + webhookId + '/logs';
        if (cursor) {
            url += '?cursor=' + encodeURIComponent(cursor);
        }

        const rcJson = await callApi(url, 'GET', {}, {});
        Tdebug && console.log('fetchLogs:', rcJson);

        setLogsLoading(false);

        if (rcJson.httpStatus === 200 || rcJson.httpStatus === '200') {
            const newLogs = rcJson.respBody.logs || [];
            if (cursor) {
                setLogs(prev => [...prev, ...newLogs]);
            } else {
                setLogs(newLogs);
            }
            setLogsCursor(rcJson.respBody.cursor || null);
            setHasMoreLogs(!!rcJson.respBody.cursor);
        } else if (rcJson.disposition !== 'empty') {
            toast.error('Failed to load logs');
        }
    };

    const openEditModal = (webhook) => {
        setSelectedWebhook(webhook);
        setFormName(webhook.name || '');
        setFormUrl(webhook.url || '');
        setFormEvents(webhook.events || []);
        setFormEnabled(webhook.isEnabled !== false);
        setFormErrors({});
        setShowEditModal(true);
    };

    const openDeleteModal = (webhook) => {
        setSelectedWebhook(webhook);
        setShowDeleteModal(true);
    };

    const handleEventToggle = (eventType) => {
        if (formEvents.includes(eventType)) {
            setFormEvents(formEvents.filter(e => e !== eventType));
        } else {
            setFormEvents([...formEvents, eventType]);
        }
    };

    const copySecret = async () => {
        try {
            await navigator.clipboard.writeText(newWebhookSecret);
            setSecretCopied(true);
            setTimeout(() => setSecretCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const formatEventName = (eventType) => {
        if (!eventType) return '';
        return eventType
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
            .replace('.', ' - ');
    };

    const formatTimestamp = (ts) => {
        if (!ts) return '-';
        const date = new Date(ts * 1000);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hr ago`;
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusBadge = (log) => {
        if (log.status === 'success') {
            return <span className="badge-modern badge-modern-success"><FaCheck className="me-1" />Success</span>;
        } else if (log.status === 'pending' || log.status === 'retrying') {
            return <span className="badge-modern badge-modern-warning"><FaRedo className="me-1" />Retry {log.retryCount}/4</span>;
        } else {
            return <span className="badge-modern badge-modern-error"><FaTimes className="me-1" />Failed</span>;
        }
    };

    const truncateUrl = (url, maxLength = 40) => {
        if (!url) return '';
        return url.length > maxLength ? url.substring(0, maxLength) + '...' : url;
    };

    const btnBase = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px',
        fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
        transition: 'all 150ms ease',
    };

    // Loading state
    if (loading) {
        return (
            <div className="app-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <div style={{ textAlign: 'center' }}>
                    <Spinner animation="border" style={{ width: '2rem', height: '2rem', color: 'var(--accent-primary)' }} />
                    <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-tertiary)' }}>Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="app-content animate-in">
            {/* Header */}
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                    <h1 className="dashboard-title">Automations</h1>
                    <p className="dashboard-subtitle">
                        Connect your website events to external automation tools
                    </p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => { resetForm(); setShowCreateModal(true); }}
                    style={{ ...btnBase, padding: '8px 16px', background: 'var(--accent-gradient)', color: 'white' }}
                >
                    <FaPlus />
                    Add Webhook
                </Button>
            </div>

            {/* Webhooks List */}
            {webhooks.length === 0 ? (
                <div className="bento-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: 'var(--radius-lg)', background: 'var(--accent-soft)', color: 'var(--accent-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                        <FaBolt size={24} />
                    </div>
                    <h4 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>No webhooks configured</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', maxWidth: '420px', margin: '0 auto 20px' }}>
                        Create a webhook to send your contact form submissions to Zapier, n8n, Make.com, or your own API.
                    </p>
                    <Button
                        variant="primary"
                        onClick={() => { resetForm(); setShowCreateModal(true); }}
                        style={{ ...btnBase, padding: '8px 16px', background: 'var(--accent-gradient)', color: 'white' }}
                    >
                        <FaPlus />
                        Create Your First Webhook
                    </Button>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {webhooks.map((webhook) => (
                        <div className="bento-card" key={webhook.webhookId}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                    <Form.Check
                                        type="switch"
                                        checked={webhook.isEnabled}
                                        onChange={() => handleToggleEnabled(webhook)}
                                        disabled={togglingWebhook === webhook.webhookId}
                                        className="mt-1"
                                        style={{ transform: 'scale(1.2)' }}
                                    />
                                    <div>
                                        <h5 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {webhook.name}
                                            {!webhook.isEnabled && (
                                                <span className="badge-modern">Disabled</span>
                                            )}
                                        </h5>
                                        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px', wordBreak: 'break-all' }}>
                                            {truncateUrl(webhook.url, 60)}
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {(webhook.events || []).map(evt => (
                                                <span key={evt} className="badge-modern badge-modern-primary">
                                                    {formatEventName(evt)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                    <Button
                                        size="sm"
                                        onClick={() => handleSendTestEvent(webhook)}
                                        disabled={testingWebhook === webhook.webhookId || !webhook.isEnabled}
                                        title="Send test event"
                                        style={{ ...btnBase, padding: '7px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                    >
                                        {testingWebhook === webhook.webhookId ? (
                                            <Spinner size="sm" animation="border" />
                                        ) : (
                                            <FaPaperPlane />
                                        )}
                                        <span className="d-none d-md-inline">Test</span>
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => handleOpenLogs(webhook)}
                                        title="View delivery logs"
                                        style={{ ...btnBase, padding: '7px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                    >
                                        <FaHistory />
                                        <span className="d-none d-md-inline">Logs</span>
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => openEditModal(webhook)}
                                        title="Edit webhook"
                                        style={{ ...btnBase, width: '34px', height: '34px', padding: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                    >
                                        <FaEdit />
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => openDeleteModal(webhook)}
                                        title="Delete webhook"
                                        style={{ ...btnBase, width: '34px', height: '34px', padding: 0, background: 'var(--error-soft)', border: '1px solid var(--error-soft)', color: 'var(--error)' }}
                                    >
                                        <FaTrash />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Info callout */}
            <div className="bento-card" style={{ marginTop: '20px', background: 'var(--accent-soft)', border: '1px solid var(--accent-soft)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <FaBolt style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>Connect to Automation Tools</div>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                            Webhooks send your contact form submissions to external services like Zapier, n8n, Make.com, or your own API endpoint.
                        </p>
                        <Button
                            size="sm"
                            href="https://www.turbosteed.com/blogs/webhooks"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ ...btnBase, padding: '7px 14px', background: 'var(--accent-gradient)', color: 'white', textDecoration: 'none' }}
                        >
                            <FaExternalLinkAlt />
                            View Integration Guide
                        </Button>
                    </div>
                </div>
            </div>

            {/* Create Webhook Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered keyboard={false} dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Add Webhook</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Webhook Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Zapier - Contact Form"
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                isInvalid={!!formErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.name}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Webhook URL</Form.Label>
                            <Form.Control
                                type="url"
                                placeholder="https://hooks.zapier.com/..."
                                value={formUrl}
                                onChange={(e) => setFormUrl(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                isInvalid={!!formErrors.url}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.url}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                The URL where events will be sent via HTTP POST
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Events to Send</Form.Label>
                            {availableEvents.length === 0 ? (
                                <div className="text-muted">Loading events...</div>
                            ) : (
                                availableEvents.map(evt => (
                                    <Form.Check
                                        key={evt.type}
                                        type="checkbox"
                                        label={evt.label || formatEventName(evt.type)}
                                        checked={formEvents.includes(evt.type)}
                                        onChange={() => handleEventToggle(evt.type)}
                                        className="mb-2"
                                    />
                                ))
                            )}
                            {formErrors.events && (
                                <div className="text-danger small">{formErrors.events}</div>
                            )}
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateWebhook} disabled={formSubmitting}>
                        {formSubmitting ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                        Create Webhook
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Webhook Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered keyboard={false} dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Webhook</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Webhook Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Zapier - Contact Form"
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                isInvalid={!!formErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.name}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Webhook URL</Form.Label>
                            <Form.Control
                                type="url"
                                placeholder="https://hooks.zapier.com/..."
                                value={formUrl}
                                onChange={(e) => setFormUrl(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                isInvalid={!!formErrors.url}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.url}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Events to Send</Form.Label>
                            {availableEvents.map(evt => (
                                <Form.Check
                                    key={evt.type}
                                    type="checkbox"
                                    label={evt.label || formatEventName(evt.type)}
                                    checked={formEvents.includes(evt.type)}
                                    onChange={() => handleEventToggle(evt.type)}
                                    className="mb-2"
                                />
                            ))}
                            {formErrors.events && (
                                <div className="text-danger small">{formErrors.events}</div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                label="Enabled"
                                checked={formEnabled}
                                onChange={(e) => setFormEnabled(e.target.checked)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleEditWebhook} disabled={formSubmitting}>
                        {formSubmitting ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Webhook</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete <strong>{selectedWebhook?.name}</strong>?</p>
                    <p className="text-muted small">This action cannot be undone. All delivery logs will also be deleted.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteWebhook} disabled={formSubmitting}>
                        {formSubmitting ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                        Delete Webhook
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Signing Secret Modal (shown after creation) */}
            <Modal show={showSecretModal} onHide={() => { setShowSecretModal(false); setNewWebhookSecret(''); }} centered dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Webhook Created</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <strong>Save your signing secret!</strong> You won't be able to see it again.
                    </Alert>
                    <Form.Label>Signing Secret</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type={showSecret ? 'text' : 'password'}
                            value={newWebhookSecret}
                            readOnly
                        />
                        <Button
                            variant="outline-secondary"
                            onClick={() => setShowSecret(!showSecret)}
                        >
                            {showSecret ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={copySecret}
                        >
                            {secretCopied ? <FaCheck /> : <FaCopy />}
                        </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                        Use this secret to verify webhook signatures. See the integration guide for details.
                    </Form.Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => { setShowSecretModal(false); setNewWebhookSecret(''); }}>
                        I've Saved My Secret
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delivery Logs Modal */}
            <Modal show={showLogsModal} onHide={() => setShowLogsModal(false)} size="lg" centered dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delivery Logs - {selectedWebhook?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {logsLoading && logs.length === 0 ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <FaHistory size={32} className="mb-2" />
                            <p>No delivery logs yet</p>
                        </div>
                    ) : (
                        <div className="webhook-logs-split">
                            {/* Left: event list */}
                            <div className="webhook-logs-list">
                                {logs.map((log, idx) => (
                                    <button
                                        key={log.eventId || idx}
                                        type="button"
                                        className={`webhook-log-row${selectedLogIdx === idx ? ' active' : ''}`}
                                        onClick={() => setSelectedLogIdx(idx)}
                                    >
                                        <div className="webhook-log-row-top">
                                            {getStatusBadge(log)}
                                            <span className="webhook-log-time">{formatTimestamp(log.createdAt)}</span>
                                        </div>
                                        <div className="webhook-log-event">
                                            {formatEventName(log.eventType || '')}
                                            {log.isTest && <span className="badge-modern badge-modern-primary ms-2">TEST</span>}
                                        </div>
                                    </button>
                                ))}
                                {hasMoreLogs && (
                                    <div className="text-center mt-2">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => fetchLogs(selectedWebhook.webhookId, logsCursor)}
                                            disabled={logsLoading}
                                        >
                                            {logsLoading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                                            Load More
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Right: detail pane */}
                            <div className="webhook-logs-detail">
                                {(() => {
                                    const log = logs[selectedLogIdx];
                                    if (!log) return null;
                                    return (
                                        <>
                                            <div className="webhook-detail-head">
                                                <div className="webhook-detail-event">
                                                    {formatEventName(log.eventType || '')}
                                                    {log.isTest && <span className="badge-modern badge-modern-primary ms-2">TEST</span>}
                                                </div>
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    {getStatusBadge(log)}
                                                    <span className="text-muted small">{formatTimestamp(log.createdAt)}</span>
                                                </div>
                                                <div className="text-muted small mt-1" style={{ wordBreak: 'break-all' }}>{log.eventId}</div>
                                            </div>
                                            {log.responseBody && (
                                                <div className="mt-3">
                                                    <strong>Response:</strong>
                                                    <pre className="p-2 rounded mt-1 small" style={{ maxHeight: '160px', overflow: 'auto', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                                                        {typeof log.responseBody === 'string' ? log.responseBody : JSON.stringify(log.responseBody, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                            {log.requestPayload && (
                                                <div className="mt-2">
                                                    <strong>Payload:</strong>
                                                    <pre className="p-2 rounded mt-1 small" style={{ maxHeight: '200px', overflow: 'auto', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                                                        {typeof log.requestPayload === 'string' ? log.requestPayload : JSON.stringify(log.requestPayload, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                            {!log.responseBody && !log.requestPayload && (
                                                <div className="text-muted small mt-3">No response or payload recorded for this event.</div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowLogsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StDashAutomations;
